import React, { useRef } from 'react';

interface ReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: any;
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({ isOpen, onClose, transaction }) => {
  const receiptRef = useRef<HTMLDivElement>(null);

  if (!isOpen || !transaction) return null;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatAmount = (amount: number, type: string) => {
    const prefix = type === 'expense' ? '-' : '+';
    return `${prefix}$${Number(amount).toFixed(2)}`;
  };

  const receiptNumber = transaction.uuid
    ? transaction.uuid.slice(0, 8).toUpperCase()
    : `TXN${transaction.id}`;

  const handlePrint = () => {
    const printContent = receiptRef.current?.innerHTML;
    if (!printContent) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${receiptNumber}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #fff; color: #222; }
            .receipt-outer { max-width: 520px; margin: 32px auto; padding: 40px; border: 1px solid #e5e7eb; border-radius: 12px; }
            .logo-row { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
            .logo-row img { height: 48px; }
            .logo-row span { font-size: 22px; font-weight: 700; color: #402d22; letter-spacing: 0.5px; }
            .divider { border: none; border-top: 1px solid #e5e7eb; margin: 16px 0; }
            .divider-bold { border: none; border-top: 2px solid #d1d5db; margin: 16px 0; }
            .receipt-title { font-size: 18px; font-weight: 700; color: #111; margin-bottom: 2px; }
            .receipt-number { font-size: 13px; color: #6b7280; margin-bottom: 16px; }
            .section-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #9ca3af; margin-bottom: 8px; }
            table { width: 100%; border-collapse: collapse; }
            td { padding: 6px 0; font-size: 14px; }
            td:last-child { text-align: right; font-weight: 500; }
            .amount-row td { font-size: 18px; font-weight: 700; padding-top: 12px; }
            .amount-income { color: #15803d; }
            .amount-expense { color: #dc2626; }
            .badge { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }
            .badge-income { background: #dcfce7; color: #15803d; }
            .badge-expense { background: #fee2e2; color: #dc2626; }
            .footer-note { font-size: 11px; color: #9ca3af; text-align: center; margin-top: 20px; line-height: 1.6; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const handleSavePdf = () => {
    handlePrint();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg relative flex flex-col max-h-[90vh]">
        {/* Modal header controls */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100">
          <h2 className="text-lg font-bold text-secondary-900">Transaction Receipt</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrint}
              title="Print Receipt"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary-100 hover:bg-secondary-200 text-secondary-700 text-sm font-medium transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            <button
              type="button"
              onClick={handleSavePdf}
              title="Save as PDF"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h4a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
              Save PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="ml-1 text-gray-400 hover:text-gray-700 text-2xl leading-none"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Receipt content */}
        <div className="overflow-y-auto flex-1 p-6">
          <div ref={receiptRef}>
            <div className="receipt-outer max-w-full border border-gray-200 rounded-xl p-8">
              {/* Logo & Brand */}
              <div className="logo-row flex items-center gap-3 mb-1">
                <img
                  src="/img/core-img/logo-black.png"
                  alt="SoundX724 Logo"
                  className="h-10 object-contain"
                />
                <span className="text-xl font-bold text-secondary-900 tracking-wide">SoundX724</span>
              </div>
              <hr className="divider my-4 border-gray-200" />

              {/* Title */}
              <div className="receipt-title text-lg font-bold text-gray-900">Transaction Receipt</div>
              <div className="receipt-number text-xs text-gray-400 mb-4">Receipt #{receiptNumber}</div>

              {/* Type Badge */}
              <div className="mb-4">
                <span className={`badge inline-block px-3 py-0.5 rounded-full text-xs font-semibold ${
                  transaction.type === 'expense'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {transaction.type?.charAt(0).toUpperCase() + transaction.type?.slice(1)}
                </span>
              </div>

              {/* Details table */}
              <div className="section-label text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Details</div>
              <table className="w-full text-sm">
                <tbody>
                  {transaction.transaction_date && (
                    <tr>
                      <td className="py-1.5 text-gray-500">Date</td>
                      <td className="py-1.5 text-right font-medium text-gray-800">{formatDate(transaction.transaction_date)}</td>
                    </tr>
                  )}
                  {transaction.from_to && (
                    <tr>
                      <td className="py-1.5 text-gray-500">From / To</td>
                      <td className="py-1.5 text-right font-medium text-gray-800">{transaction.from_to}</td>
                    </tr>
                  )}
                  {transaction.description && (
                    <tr>
                      <td className="py-1.5 text-gray-500 align-top">Description</td>
                      <td className="py-1.5 text-right font-medium text-gray-800">{transaction.description}</td>
                    </tr>
                  )}
                  {transaction.event_title && (
                    <tr>
                      <td className="py-1.5 text-gray-500">Event</td>
                      <td className="py-1.5 text-right font-medium text-gray-800">{transaction.event_title}</td>
                    </tr>
                  )}
                  {transaction.username && (
                    <tr>
                      <td className="py-1.5 text-gray-500">Recorded by</td>
                      <td className="py-1.5 text-right font-medium text-gray-800">{transaction.username}</td>
                    </tr>
                  )}
                  {transaction.created_at && (
                    <tr>
                      <td className="py-1.5 text-gray-500">Recorded on</td>
                      <td className="py-1.5 text-right font-medium text-gray-800">{formatDate(transaction.created_at)}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <hr className="divider-bold my-4 border-t-2 border-gray-200" />

              {/* Amount row */}
              <table className="w-full">
                <tbody>
                  <tr className="amount-row">
                    <td className="text-base font-bold text-gray-900">Total Amount</td>
                    <td className={`text-right text-xl font-bold ${
                      transaction.type === 'expense' ? 'amount-expense text-red-600' : 'amount-income text-green-700'
                    }`}>
                      {formatAmount(transaction.amount, transaction.type)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <hr className="divider my-4 border-gray-200" />

              {/* Footer note */}
              <div className="footer-note text-center text-xs text-gray-400 mt-4 leading-relaxed">
                This is a system generated receipt. No signatures are required.<br />
                © {new Date().getFullYear()} SoundX724. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
